import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ExternalLink, Trophy, Users, Package, X, ChevronLeft, ChevronRight, Expand, FileDown } from "lucide-react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { PORTFOLIO_CATEGORIES, PORTFOLIO_PILLARS, PILLARED_ITEMS } from "@/content/portfolio";
import Seo from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];
const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

function Gallery() {
  const [tab, setTab] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const filtered = PILLARED_ITEMS.filter((it) => tab === "all" || it.pillar === tab);
  const catLabel = (id) => PORTFOLIO_CATEGORIES.find((c) => c.id === id)?.label || id;
  const pillarLabel = (id) => PORTFOLIO_PILLARS.find((p) => p.id === id)?.label || id;
  const counts = PORTFOLIO_PILLARS.map((p) => ({ ...p, n: PILLARED_ITEMS.filter((it) => it.pillar === p.id).length })).filter((p) => p.n > 0);

  const close = useCallback(() => setLightbox(null), []);
  const step = useCallback((dir) => {
    setLightbox((cur) => (cur == null ? cur : (cur + dir + filtered.length) % filtered.length));
  }, [filtered.length]);

  useEffect(() => {
    if (lightbox == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, close, step]);

  useEffect(() => { setLightbox(null); }, [tab]);

  return (
    <section data-testid="portfolio-gallery">
      <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
        <div>
          <p className="font-mono text-[10px] tracking-[0.4em] text-nc-blue uppercase mb-2">The Work</p>
          <h2 className="font-sans font-bold uppercase tracking-tight text-2xl md:text-3xl text-zinc-50">
            {PILLARED_ITEMS.length} Pieces // {counts.length} AAA Pillars
          </h2>
        </div>
      </div>

      <div data-testid="gallery-tabs" className="flex flex-wrap gap-2 mb-4">
        <button
          data-testid="gallery-tab-all"
          onClick={() => setTab("all")}
          className={`font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors duration-300 ${tab === "all" ? "text-nc-blue border-nc-blue/60 bg-nc-blue/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}
        >
          All ({PILLARED_ITEMS.length})
        </button>
        {counts.map((c) => (
          <button
            key={c.id}
            data-testid={`gallery-tab-${c.id}`}
            onClick={() => setTab(c.id)}
            className={`font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors duration-300 ${tab === c.id ? "text-nc-orange border-nc-orange/60 bg-nc-orange/10" : "text-zinc-400 border-white/10 hover:border-white/30"}`}
          >
            {c.label} ({c.n})
          </button>
        ))}
      </div>
      {tab !== "all" && (
        <p data-testid="pillar-tagline" className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-6">
          {PORTFOLIO_PILLARS.find((p) => p.id === tab)?.code} // {PORTFOLIO_PILLARS.find((p) => p.id === tab)?.tagline}
        </p>
      )}
      {tab === "all" && <div className="mb-6" />}

      <motion.div layout className="columns-2 md:columns-3 xl:columns-4 gap-4 [column-fill:balance]">
        <AnimatePresence mode="popLayout">
          {filtered.map((it, i) => (
            <motion.figure
              layout
              key={it.src}
              data-testid={`gallery-item-${i + 1}`}
              className="group relative mb-4 break-inside-avoid border border-white/10 overflow-hidden cursor-zoom-in bg-black/40 hover:border-nc-blue/50 transition-colors duration-300"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.5, ease: EASE }}
              onClick={() => setLightbox(i)}
            >
              <img src={it.src} alt={it.title} loading="lazy" className="w-full h-auto group-hover:scale-[1.03] transition-transform duration-700" />
              <figcaption className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                <span className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase">{pillarLabel(it.pillar)}</span>
                <span className="font-sans font-semibold uppercase text-sm text-zinc-50 mt-1 flex items-center gap-2">
                  {it.title} <Expand className="w-3.5 h-3.5 text-zinc-400" />
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {lightbox != null && filtered[lightbox] && (
          <motion.div
            data-testid="gallery-lightbox"
            className="fixed inset-0 z-[100] bg-black/97 flex items-center justify-center p-3 sm:p-6 lg:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          >
            <button data-testid="gallery-lightbox-close" onClick={close} className="absolute top-4 right-4 w-10 h-10 border border-white/20 flex items-center justify-center text-zinc-300 hover:text-white hover:border-white/50 transition-colors duration-300 z-10">
              <X className="w-5 h-5" />
            </button>
            <button data-testid="gallery-lightbox-prev" onClick={(e) => { e.stopPropagation(); step(-1); }} className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 border border-white/20 flex items-center justify-center text-zinc-300 hover:text-nc-blue hover:border-nc-blue/60 transition-colors duration-300 z-10">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button data-testid="gallery-lightbox-next" onClick={(e) => { e.stopPropagation(); step(1); }} className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 border border-white/20 flex items-center justify-center text-zinc-300 hover:text-nc-blue hover:border-nc-blue/60 transition-colors duration-300 z-10">
              <ChevronRight className="w-5 h-5" />
            </button>

            <div className="w-full max-w-7xl h-full max-h-[88vh] grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-5" onClick={(e) => e.stopPropagation()}>
              {/* Image stage */}
              <div className="relative bg-[#0a0a0a] border border-white/15 overflow-hidden flex items-center justify-center min-h-[40vh]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={filtered[lightbox].src}
                    data-testid="gallery-lightbox-image"
                    src={filtered[lightbox].src}
                    alt={filtered[lightbox].title}
                    className="w-full h-full object-contain"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </AnimatePresence>
                <span className="absolute top-4 left-4 font-mono text-[9px] tracking-[0.3em] text-zinc-400 bg-black/60 backdrop-blur-sm border border-white/15 px-3 py-1.5 uppercase">
                  Case Study // {String(lightbox + 1).padStart(2, "0")} / {String(filtered.length).padStart(2, "0")}
                </span>
              </div>

              {/* Case study panel */}
              <aside data-testid="case-study-panel" className="border border-white/15 bg-[#0a0a0a] p-6 flex flex-col overflow-y-auto">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={filtered[lightbox].title}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col flex-1"
                  >
                    <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase">{pillarLabel(filtered[lightbox].pillar)} // {catLabel(filtered[lightbox].category)}</p>
                    <h3 data-testid="gallery-lightbox-caption" className="font-sans font-bold uppercase tracking-tight text-xl text-zinc-50 mt-2 leading-tight">
                      {filtered[lightbox].title}
                    </h3>
                    <div className="h-px bg-white/10 my-5" />
                    <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-600 uppercase mb-2">About this piece</p>
                    <p data-testid="gallery-lightbox-desc" className="font-mono text-xs text-zinc-400 leading-relaxed">
                      {filtered[lightbox].desc}
                    </p>
                    <div className="h-px bg-white/10 my-5" />
                    <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-600 uppercase mb-3">Delivered by</p>
                    <div className="flex flex-col gap-2.5">
                      <div className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-nc-blue shrink-0" />
                        <span className="font-mono text-xs text-zinc-300">Specpixel Entertainment — Melbourne, AU</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-nc-blue shrink-0" />
                        <span className="font-mono text-xs text-zinc-300">Game-ready // production-proven</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 bg-nc-orange shrink-0" />
                        <span className="font-mono text-xs text-zinc-300">Available for commission work</span>
                      </div>
                    </div>
                    <div className="mt-auto pt-6">
                      <Link to="/contact" data-testid="case-study-cta" onClick={close} className="w-full bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-5 py-3.5 flex items-center justify-center gap-2 transition-colors duration-300">
                        Commission work like this <ArrowRight className="w-4 h-4" />
                      </Link>
                      <div className="flex items-center justify-between mt-4">
                        <button data-testid="case-study-prev" onClick={() => step(-1)} className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 hover:text-nc-blue uppercase transition-colors flex items-center gap-1.5">
                          <ChevronLeft className="w-3.5 h-3.5" /> Prev
                        </button>
                        <span className="font-mono text-[9px] tracking-[0.25em] text-zinc-600 uppercase">{lightbox + 1} / {filtered.length}</span>
                        <button data-testid="case-study-next" onClick={() => step(1)} className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 hover:text-nc-blue uppercase transition-colors flex items-center gap-1.5">
                          Next <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </aside>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

export default function PortfolioPage() {
  const [fabCount, setFabCount] = useState(0);
  useEffect(() => {
    api.get("/fab/products").then((r) => setFabCount(r.data.length)).catch(() => {});
  }, []);

  return (
    <div data-testid="portfolio-page" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo
        title="Game Development Portfolio — UE5, Enfusion & AAA Art"
        description="Game development portfolio from Specpixel Entertainment: UE5 environments, AAA characters, Arma Reforger (Enfusion) work, cinematics, hard surface vehicles and technical art. Shipped titles New Citizen and Living Dead."
        path="/portfolio"
        keywords="game development portfolio, game art portfolio, UE5 environment art, unreal engine portfolio, arma reforger portfolio, character modeling portfolio, game cinematics studio"
      />
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <p className="font-mono text-xs tracking-[0.4em] text-nc-orange uppercase mb-4">Selected Works</p>
          <h1 data-testid="portfolio-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl text-zinc-50">
            Portfolio
          </h1>
          <p className="font-mono text-sm text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            Real production work pulled straight from the studio vault — shipped titles, client deliveries
            and {PILLARED_ITEMS.length} pieces of craft across five AAA pillars.
          </p>
          <motion.a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/portfolio/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="portfolio-pdf-download"
            className="mt-6 inline-flex items-center gap-3 border border-nc-orange/50 text-nc-orange hover:bg-nc-orange hover:text-white font-sans font-semibold uppercase tracking-[0.2em] text-xs px-6 py-3 transition-colors duration-300"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <FileDown className="w-4 h-4" /> Download Portfolio PDF
          </motion.a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-16 w-full flex flex-col gap-20">
        <section data-testid="portfolio-games">
          <div className="flex items-center gap-4 mb-8">
            <Trophy className="w-5 h-5 text-nc-blue" />
            <h2 className="font-sans font-semibold uppercase tracking-[0.3em] text-lg text-zinc-100">Flagship Titles</h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="border border-nc-blue/30 bg-nc-blue/5 p-8 relative overflow-hidden group">
              <div className="absolute inset-0 grid-texture opacity-30" />
              <div className="relative">
                <img src="/assets/newcitizen-logo-blue.png" alt="New Citizen" className="h-20 w-auto mb-6" />
                <span className="font-mono text-[9px] tracking-[0.25em] text-nc-green border border-nc-green/40 bg-nc-green/10 px-2 py-1 uppercase">Out Now — Early Access</span>
                <h3 className="font-sans font-bold uppercase text-2xl text-zinc-50 mt-4">New Citizen</h3>
                <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed">
                  A new era of police roleplay in Unreal Engine 5. Open-world multiplayer life simulation — join the sheriff's
                  department, respond to callouts and live another life. V1.0 is a ground-up rework taking the game from Alpha to Beta.
                </p>
                <Link to="/new-citizen" data-testid="portfolio-nc-link" className="font-mono text-[10px] tracking-[0.25em] text-nc-blue uppercase mt-6 inline-flex items-center gap-2 hover:underline">
                  V1.0 Release Roadmap <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.div>

            <motion.a {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }} href="https://store.steampowered.com/app/1084840/Living_Dead/" target="_blank" rel="noopener noreferrer" data-testid="portfolio-ld-link" className="border border-nc-orange/30 group block">
              <div className="relative overflow-hidden aspect-video">
                <img src="/assets/portfolio/studio/livingdead-screenshot.png" alt="Living Dead gameplay" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              </div>
              <div className="p-6 bg-nc-orange/5 flex items-center justify-between gap-4">
                <div>
                  <span className="font-mono text-[9px] tracking-[0.25em] text-zinc-400 border border-white/15 px-2 py-1 uppercase">Shipped — PC / Steam</span>
                  <h3 className="font-sans font-bold uppercase text-2xl text-zinc-50 mt-3 group-hover:text-nc-orange transition-colors duration-300">Living Dead</h3>
                  <p className="font-mono text-xs text-zinc-400 mt-2 leading-relaxed">
                    Our zombie survival title for PC — built and shipped by the Specpixel team.
                  </p>
                </div>
                <ArrowUpRight className="w-6 h-6 text-zinc-500 group-hover:text-nc-orange group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300 shrink-0" />
              </div>
            </motion.a>
          </div>
        </section>

        <Gallery />

        <section data-testid="portfolio-client">
          <div className="flex items-center gap-4 mb-8">
            <Users className="w-5 h-5 text-nc-orange" />
            <h2 className="font-sans font-semibold uppercase tracking-[0.3em] text-lg text-zinc-100">Client Deliveries</h2>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { title: "PaperPlanes — Localization", tag: "UE5 // LOCALIZATION", body: "Full localization pipeline for 11+ languages in Unreal Engine 5 — roughly 3,000 words translated per language, integrated and shipped." },
              { title: "Confidential — Under NDA", tag: "MULTIPLAYER // PROGRAMMING", body: "Ongoing client engagements under non-disclosure. Gameplay programming, networking and optimization work for partner studios." },
              { title: "Your Project Here", tag: "NOW BOOKING", body: "We take on a limited number of outsourcing contracts at a time to keep quality high. Quotes returned within 24 hours.", cta: true },
            ].map((c, i) => (
              <motion.div key={c.title} {...fadeUp} transition={{ duration: 0.5, delay: i * 0.08 }} className="border border-white/10 bg-black/40 p-6 flex flex-col gap-3 hover:border-white/30 transition-colors duration-300">
                <span className="font-mono text-[9px] tracking-[0.25em] text-nc-blue uppercase">{c.tag}</span>
                <h3 className="font-sans font-bold uppercase text-base text-zinc-100">{c.title}</h3>
                <p className="font-mono text-xs text-zinc-500 leading-relaxed flex-1">{c.body}</p>
                {c.cta && (
                  <Link to="/contact" className="font-mono text-[10px] tracking-[0.25em] text-nc-orange uppercase inline-flex items-center gap-2 hover:underline">
                    Start a project <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        <section data-testid="portfolio-assets">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <Link to="/asset-packs" className="border border-white/10 bg-black/40 p-8 flex flex-col md:flex-row md:items-center gap-6 hover:border-nc-blue/50 transition-colors duration-300 group">
              <div className="w-14 h-14 border border-nc-blue/40 bg-nc-blue/10 flex items-center justify-center shrink-0">
                <Package className="w-6 h-6 text-nc-blue" />
              </div>
              <div className="flex-1">
                <h3 className="font-sans font-bold uppercase text-xl text-zinc-50 group-hover:text-nc-blue transition-colors duration-300">
                  Fab Asset Library{fabCount > 0 ? ` — ${fabCount}+ Products` : ""}
                </h3>
                <p className="font-mono text-xs text-zinc-500 mt-2 leading-relaxed">
                  AAA character packs, law-enforcement uniforms, historical kits and vehicles — production-proven assets used by studios worldwide.
                </p>
              </div>
              <span className="font-mono text-[10px] tracking-[0.25em] text-nc-blue uppercase flex items-center gap-2 shrink-0">
                Browse the store <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </Link>
          </motion.div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
